import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {
  FirebaseMessageRoomContentData,
  FirebaseMessageRoomItem,
  FirebaseMessageRoomType,
  MessageSendMessageResponse,
} from 'types'
import {useCheckLastDelete, useDidMount} from 'utils'
import {requestData} from 'services'
import GiftShopMessageRoomContent from './GiftShopMessageRoomContent'
import GiftShopMessageRoomInput from './GiftShopMessageRoomInput'
import GiftShopMessageRoomInputDisabled from './GiftShopMessageRoomInputDisabled'

const StyledContentContainer = styled.div`
  ${({theme}) => ({backgroundColor: theme.white_3})}
  display: flex;
  flex: 1;
  flex-direction: column;
`
const StyledContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`
export interface GiftShopMessageRoomProps {
  participantId: string
  roomId: string
  roomType: FirebaseMessageRoomType
  content?: FirebaseMessageRoomContentData
  onSendMessage?(
    message: string,
    roomId: string,
    memberId: string,
    contentId?: string,
    invoiceId?: string,
  ): Promise<MessageSendMessageResponse | null>
  isContentClickable?: boolean
  isBanned: boolean
}
export default function GiftShopMessageRoom({
  participantId,
  roomId,
  roomType,
  content,
  isContentClickable,
  onSendMessage,
  isBanned,
}: GiftShopMessageRoomProps) {
  const stateTempMessages = useState<
    ReadonlyArray<FirebaseMessageRoomItem<'chat'>>
  >([])
  const {initialize, roomLastDelete} = useCheckLastDelete(roomId)
  const [blocked, setBlocked] = useState(false)
  const [loadBlockStatus, setLoadBlockStatus] = useState(false)

  const handleUnblock = useCallback(() => {
    requestData('forkygram_delete_unblock_account', {
      params: {blocked_id: participantId},
      onRequestSuccess: ({status}) => {
        if (status === 200) {
          setBlocked(false)
        }
      },
    })
  }, [participantId])

  const handleCheckBlock = useCallback(() => {
    setLoadBlockStatus(true)
    requestData('forkygram_get_block_status', {
      params: {member_id: participantId},
      onRequestReceived: () => setLoadBlockStatus(false),
      onRequestSuccess: ({status, data: {result}}) => {
        if (status === 200) {
          setBlocked(result[0].is_blocked)
        }
      },
    })
  }, [participantId])

  const handleRenderContent = useMemo(
    () =>
      initialize && (
        <StyledContentContainer>
          <GiftShopMessageRoomContent
            roomId={roomId}
            roomType={roomType}
            stateTempMessages={stateTempMessages}
            roomLastDelete={roomLastDelete}
          />
        </StyledContentContainer>
      ),
    [initialize, roomId, roomType, stateTempMessages, roomLastDelete],
  )

  const handleRenderInput = useMemo(
    () =>
      onSendMessage ? (
        isBanned ? (
          <GiftShopMessageRoomInputDisabled type="banned" />
        ) : blocked ? (
          <GiftShopMessageRoomInputDisabled
            type="blocked"
            onClickButton={handleUnblock}
          />
        ) : (
          <GiftShopMessageRoomInput
            participantId={participantId}
            roomId={roomId}
            content={content}
            isContentClickable={isContentClickable}
            stateTempMessages={stateTempMessages}
            onSendMessage={onSendMessage}
          />
        )
      ) : (
        <GiftShopMessageRoomInputDisabled type="system" />
      ),
    [
      blocked,
      content,
      handleUnblock,
      isBanned,
      isContentClickable,
      onSendMessage,
      participantId,
      roomId,
      stateTempMessages,
    ],
  )

  useDidMount(() => {
    if (roomType !== 'system') {
      handleCheckBlock()
    } else setLoadBlockStatus(false)
  })

  return (
    <StyledContainer>
      {handleRenderContent}
      {!loadBlockStatus && handleRenderInput}
    </StyledContainer>
  )
}
